import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function PaintersSandownPage() {
  const [activeTab, setActiveTab] = useState('overview');

  useSEO({
    title: 'Sandown Painters | Professional Painting Services | 17+ Years Experience',
    description: 'Expert painters in Sandown offering premium residential and commercial painting services. Specializing in luxury homes, estates, and high-end properties. Free quotes, quality workmanship, and professional service guaranteed.',
    keywords: 'Sandown painters, painters Sandown, painting services Sandown, house painters Sandown, residential painters, commercial painters, luxury home painters, estate painters, professional painters',
    ogImage: 'https://public.readdy.ai/ai/img_res/25f2b5a31c2ee62aeb1f1c2ac2e08a37.jpg',
    schema: [
      generateLocalBusinessSchema({
        name: 'Painters Sandown',
        description: 'Professional painting services in Sandown specializing in luxury residential and commercial properties with over 17 years of experience.',
        address: {
          streetAddress: 'Sandown',
          addressLocality: 'Cape Town',
          addressRegion: 'Western Cape',
          postalCode: '7441',
          addressCountry: 'ZA'
        },
        geo: {
          latitude: '-33.9789',
          longitude: '18.5089'
        },
        areaServed: ['Sandown', 'Bloubergstrand', 'Table View', 'Parklands', 'Big Bay', 'West Beach', 'Sunningdale'],
        priceRange: '$$-$$$',
        image: 'https://readdy.ai/api/search-image?query=professional%20painting%20company%20team%20working%20on%20luxury%20residential%20property%20modern%20equipment%20clean%20white%20background%20high%20quality%20professional%20photography&width=800&height=600&seq=sandown-business&orientation=landscape'
      }),
      generateServiceSchema({
        name: 'Residential Painting Services',
        description: 'Premium residential painting services for luxury homes and estates in Sandown',
        provider: 'Painters Sandown',
        areaServed: 'Sandown, Cape Town',
        serviceType: 'Painting Service'
      })
    ]
  });

  const services = [
    {
      title: 'Luxury Home Painting',
      description: 'Premium painting services for high-end residential properties with attention to detail and quality finishes.',
      icon: 'ri-home-5-line'
    },
    {
      title: 'Estate Painting',
      description: 'Comprehensive painting solutions for large estates and multi-property developments.',
      icon: 'ri-building-line'
    },
    {
      title: 'Interior Design Painting',
      description: 'Specialized interior painting with designer finishes, feature walls, and custom color schemes.',
      icon: 'ri-paint-brush-line'
    },
    {
      title: 'Exterior Weatherproofing',
      description: 'Durable exterior painting with weather-resistant coatings perfect for coastal properties.',
      icon: 'ri-shield-check-line'
    },
    {
      title: 'Commercial Properties',
      description: 'Professional painting services for offices, retail spaces, and commercial buildings.',
      icon: 'ri-store-2-line'
    },
    {
      title: 'Maintenance Programs',
      description: 'Regular maintenance painting programs to keep your property looking pristine year-round.',
      icon: 'ri-tools-line'
    }
  ];

  const reviews = [
    {
      name: 'Michael Thompson',
      rating: 5,
      text: 'Exceptional service from start to finish. The team painted our luxury home in Sandown and the results exceeded our expectations. Professional, punctual, and meticulous attention to detail.',
      date: '2 weeks ago'
    },
    {
      name: 'Sarah van der Merwe',
      rating: 5,
      text: 'We hired them for our estate property and couldn\'t be happier. They managed the entire project efficiently, kept everything clean, and the quality of work is outstanding. Highly recommended!',
      date: '1 month ago'
    },
    {
      name: 'David Chen',
      rating: 5,
      text: 'Best painters we\'ve worked with in Cape Town. They transformed our home with beautiful finishes and their expertise in color consultation was invaluable. Worth every cent.',
      date: '1 month ago'
    },
    {
      name: 'Jennifer Botha',
      rating: 5,
      text: 'Professional team that takes pride in their work. They painted both interior and exterior of our property and the results are stunning. Great communication throughout the project.',
      date: '2 months ago'
    },
    {
      name: 'Robert Williams',
      rating: 5,
      text: 'Excellent workmanship and attention to detail. They completed our commercial property on time and within budget. The team is reliable, skilled, and a pleasure to work with.',
      date: '2 months ago'
    },
    {
      name: 'Lisa Jacobs',
      rating: 5,
      text: 'Transformed our home beautifully! The painters were respectful, clean, and delivered exceptional quality. Their expertise in luxury home painting really shows in the final result.',
      date: '3 months ago'
    }
  ];

  const galleryImages = [
    {
      url: 'https://readdy.ai/api/search-image?query=luxury%20modern%20home%20exterior%20freshly%20painted%20white%20and%20grey%20contemporary%20architecture%20clean%20simple%20background%20professional%20photography%20bright%20daylight%20premium%20residential%20property&width=600&height=400&seq=sandown-gallery-1&orientation=landscape',
      title: 'Luxury Home Exterior'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=elegant%20interior%20living%20room%20freshly%20painted%20neutral%20tones%20modern%20furniture%20clean%20simple%20background%20professional%20photography%20natural%20lighting%20high%20end%20residential&width=600&height=400&seq=sandown-gallery-2&orientation=landscape',
      title: 'Interior Living Space'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=modern%20estate%20property%20exterior%20painted%20white%20contemporary%20design%20landscaped%20garden%20clean%20simple%20background%20professional%20photography%20bright%20natural%20light&width=600&height=400&seq=sandown-gallery-3&orientation=landscape',
      title: 'Estate Property'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=commercial%20office%20building%20exterior%20freshly%20painted%20modern%20architecture%20clean%20simple%20background%20professional%20photography%20bright%20daylight%20business%20property&width=600&height=400&seq=sandown-gallery-4&orientation=landscape',
      title: 'Commercial Building'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=luxury%20bedroom%20interior%20freshly%20painted%20soft%20colors%20elegant%20design%20clean%20simple%20background%20professional%20photography%20natural%20lighting%20premium%20residential&width=600&height=400&seq=sandown-gallery-5&orientation=landscape',
      title: 'Bedroom Interior'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=modern%20kitchen%20interior%20painted%20white%20and%20grey%20contemporary%20design%20clean%20simple%20background%20professional%20photography%20bright%20natural%20lighting%20high%20end%20home&width=600&height=400&seq=sandown-gallery-6&orientation=landscape',
      title: 'Kitchen Renovation'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-black text-white py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-2xl font-bold" style={{ color: '#e9f638' }}>
              Painters Directory
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="hover:text-gray-300 transition-colors whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-gray-300 transition-colors whitespace-nowrap">About</Link>
              <Link to="/contact" className="hover:text-gray-300 transition-colors whitespace-nowrap">Contact</Link>
              <Link to="/get-a-quote" className="px-6 py-2 rounded-lg whitespace-nowrap transition-colors" style={{ backgroundColor: '#e9f638', color: '#000' }}>
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative h-96 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=luxury%20residential%20property%20in%20sandown%20cape%20town%20modern%20architecture%20coastal%20setting%20clean%20simple%20background%20professional%20photography%20bright%20natural%20lighting%20premium%20homes&width=1920&height=600&seq=sandown-hero&orientation=landscape"
            alt="Sandown Painters"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-center px-4">
          <div className="w-full">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Painters Sandown</h1>
            <p className="text-xl md:text-2xl text-white mb-6">Premium Painting Services for Luxury Properties</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/get-a-quote"
                className="px-8 py-3 rounded-lg font-semibold transition-all hover:scale-105 whitespace-nowrap"
                style={{ backgroundColor: '#e9f638', color: '#000' }}
              >
                Get Free Quote
              </Link>
              <a
                href="tel:0219876543"
                className="px-8 py-3 bg-white text-black rounded-lg font-semibold transition-all hover:scale-105 whitespace-nowrap"
              >
                Call Now
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="border-b border-gray-200 sticky top-16 bg-white z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {['overview', 'services', 'gallery', 'reviews'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab
                    ? 'border-black text-black'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {activeTab === 'overview' && (
          <div className="space-y-12">
            {/* About Section */}
            <section>
              <h2 className="text-3xl font-bold mb-6">About Painters Sandown</h2>
              <div className="prose max-w-none space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Welcome to Painters Sandown, your premier choice for professional painting services in one of Cape Town's most prestigious coastal suburbs. With over 17 years of experience in the painting industry, we have established ourselves as the leading painting contractor for luxury residential properties, estates, and commercial buildings in the Sandown area and surrounding communities.
                </p>
                <p>
                  Sandown is renowned for its beautiful coastal location, luxury homes, and upscale properties that demand the highest standards of workmanship and attention to detail. Our team of expert painters understands the unique requirements of painting in this exclusive area, from dealing with coastal weather conditions to maintaining the aesthetic standards expected by discerning property owners. We take pride in delivering exceptional results that enhance the beauty and value of your property while providing long-lasting protection against the elements.
                </p>
                <p>
                  Our company was founded on the principles of quality craftsmanship, professional service, and customer satisfaction. Over the years, we have built a reputation for excellence by consistently delivering outstanding results on every project, whether it's a luxury home renovation, estate maintenance program, or commercial property upgrade. We understand that your property is one of your most valuable assets, and we treat every project with the care, respect, and attention it deserves.
                </p>
                <p>
                  What sets Painters Sandown apart from other painting contractors is our commitment to using only premium quality paints and materials, our team of highly skilled and experienced painters, and our meticulous attention to detail at every stage of the project. We work closely with homeowners, property managers, interior designers, and architects to ensure that every aspect of the painting project meets or exceeds expectations. From initial color consultation to final inspection, we maintain the highest standards of professionalism and workmanship.
                </p>
                <p>
                  Our expertise extends across all types of painting projects, including interior painting, exterior painting, specialty finishes, decorative painting, and protective coatings. We are particularly experienced in working with luxury properties that require sophisticated color schemes, premium finishes, and expert surface preparation. Whether you're looking to refresh a single room, repaint your entire home, or maintain a large estate property, we have the skills, experience, and resources to deliver exceptional results.
                </p>
                <p>
                  We understand that painting projects can be disruptive, which is why we work efficiently to minimize inconvenience while maintaining our high standards of quality. Our team is fully insured, professionally trained, and committed to maintaining a clean and safe work environment. We use dust-free sanding equipment, protective coverings, and proper ventilation to ensure that your property remains clean and comfortable throughout the painting process.
                </p>
                <p>
                  At Painters Sandown, we believe in building long-term relationships with our clients based on trust, reliability, and exceptional service. Many of our clients return to us for all their painting needs and recommend us to their friends, family, and colleagues. This word-of-mouth reputation is something we value highly and work hard to maintain with every project we undertake.
                </p>
                <p>
                  Our service area includes Sandown, Bloubergstrand, Table View, Parklands, Big Bay, West Beach, Sunningdale, and surrounding areas. We are familiar with the local architecture, climate conditions, and property standards, which allows us to provide expert advice and recommendations tailored to your specific needs. Whether you own a beachfront property, estate home, or commercial building, we have the expertise to deliver results that exceed your expectations.
                </p>
              </div>
            </section>

            {/* Why Choose Us */}
            <section className="bg-gray-50 rounded-lg p-8">
              <h2 className="text-3xl font-bold mb-6">Why Choose Painters Sandown?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-medal-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">17+ Years Experience</h3>
                    <p className="text-gray-600">Extensive experience in luxury residential and commercial painting projects throughout Sandown and Cape Town.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-star-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Premium Quality</h3>
                    <p className="text-gray-600">We use only the finest quality paints and materials to ensure lasting beauty and durability.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-team-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Expert Team</h3>
                    <p className="text-gray-600">Highly skilled painters with specialized training in luxury property painting and finishing techniques.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-shield-check-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Fully Insured</h3>
                    <p className="text-gray-600">Comprehensive insurance coverage for your peace of mind and property protection.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-time-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">On-Time Completion</h3>
                    <p className="text-gray-600">We respect your time and complete projects on schedule without compromising quality.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-customer-service-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Exceptional Service</h3>
                    <p className="text-gray-600">Professional, courteous service from initial consultation through project completion and beyond.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Service Areas */}
            <section>
              <h2 className="text-3xl font-bold mb-6">Service Areas</h2>
              <p className="text-gray-700 mb-4">
                We proudly serve Sandown and surrounding areas including:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['Sandown', 'Bloubergstrand', 'Table View', 'Parklands', 'Big Bay', 'West Beach', 'Sunningdale', 'Milnerton'].map((area) => (
                  <div key={area} className="flex items-center space-x-2">
                    <i className="ri-map-pin-line" style={{ color: '#e9f638' }}></i>
                    <span className="text-gray-700">{area}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {activeTab === 'services' && (
          <div className="space-y-12">
            <section>
              <h2 className="text-3xl font-bold mb-6">Our Services</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg mb-4" style={{ backgroundColor: '#e9f638' }}>
                      <i className={`${service.icon} text-2xl`}></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Detailed Services */}
            <section className="prose max-w-none">
              <h2 className="text-3xl font-bold mb-6">Comprehensive Painting Solutions</h2>
              <div className="space-y-6 text-gray-700">
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Luxury Home Painting</h3>
                  <p>
                    Our luxury home painting services are designed specifically for high-end residential properties in Sandown and surrounding areas. We understand that luxury homes require exceptional attention to detail, premium materials, and expert craftsmanship. Our team specializes in creating beautiful, sophisticated finishes that enhance the architectural features of your home while reflecting your personal style. From elegant neutral palettes to bold designer colors, we work with you to achieve the perfect look for your luxury property.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Estate Property Painting</h3>
                  <p>
                    Managing painting projects for large estates requires specialized expertise, coordination, and resources. Our estate painting services include comprehensive project management, multiple property coordination, and ongoing maintenance programs. We work with estate managers and property owners to develop customized painting schedules that maintain the pristine appearance of all buildings and structures while minimizing disruption to residents and operations.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Interior Design Painting</h3>
                  <p>
                    Transform your interior spaces with our specialized interior design painting services. We offer expert color consultation, custom color matching, specialty finishes, feature walls, and decorative painting techniques. Our painters are experienced in working with interior designers and architects to bring design visions to life with precision and artistry. Whether you're looking for subtle elegance or dramatic impact, we have the skills to deliver exceptional results.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Exterior Weatherproofing</h3>
                  <p>
                    Coastal properties in Sandown face unique challenges from salt air, wind, and moisture. Our exterior weatherproofing services provide durable protection using premium weather-resistant coatings and proper surface preparation techniques. We specialize in protecting your property from the elements while maintaining its beautiful appearance. Our exterior painting services include thorough surface preparation, crack repair, waterproofing, and application of high-quality exterior paints designed for coastal conditions.
                  </p>
                </div>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'gallery' && (
          <div>
            <h2 className="text-3xl font-bold mb-6">Our Work</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryImages.map((image, index) => (
                <div key={index} className="group relative overflow-hidden rounded-lg aspect-video">
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover object-top transition-transform group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <p className="text-white font-semibold text-lg">{image.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'reviews' && (
          <div>
            <h2 className="text-3xl font-bold mb-6">Customer Reviews</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {reviews.map((review, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{review.name}</h3>
                      <p className="text-sm text-gray-500">{review.date}</p>
                    </div>
                    <div className="flex space-x-1">
                      {[...Array(review.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill" style={{ color: '#e9f638' }}></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-700">{review.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <section className="py-16" style={{ backgroundColor: '#e9f638' }}>
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Property?</h2>
          <p className="text-xl mb-8">Get a free, no-obligation quote for your painting project today.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/get-a-quote"
              className="px-8 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              Request Free Quote
            </Link>
            <a
              href="tel:0219876543"
              className="px-8 py-3 bg-white text-black rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Call Us Now
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#e9f638' }}>Painters Sandown</h3>
              <p className="text-gray-400">Professional painting services for luxury properties in Sandown and surrounding areas.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/get-a-quote" className="text-gray-400 hover:text-white transition-colors">Get a Quote</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Luxury Home Painting</li>
                <li>Estate Painting</li>
                <li>Interior Design Painting</li>
                <li>Exterior Weatherproofing</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center space-x-2">
                  <i className="ri-phone-line"></i>
                  <span>021 987 6543</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-map-pin-line"></i>
                  <span>Sandown, Cape Town</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2024 Painters Sandown. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm text-gray-400">
              <a href="https://leaderr.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Powered by leaderr
              </a>
              <a href="https://seostudio.co.za" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                SEO by SEO Studio
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}